import { usePlanwerte } from '@/domain/controlling/hooks';
import { MonthParams } from '../../types';
import { useParams } from 'react-router-dom';
import { Abweichung, ZielstundenField } from '../../components';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { useUmsatzplanungSollstunden } from '@/domain/controlling/hooks/useUmsatzplanungSollstunden';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { toNumber } from '@/utils/toNumber/toNumber';
import dayjs from 'dayjs';

export function SollstundenAbweichungKPI({
  type,
}: {
  type: 'absatz' | 'umsatz';
}) {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { year, month } = useParams<MonthParams>();
  const { t } = useTranslation();
  const jahr = toNumber(year) ?? dayjs().get('year');
  const monat = toNumber(month) ?? dayjs().get('month') + 1;
  const { sollstunden: zielSollstunden } = useUmsatzplanungSollstunden(
    standortId,
    {
      jahr,
      monat,
    },
  );

  const { planwerte } = usePlanwerte({
    standortId,
    jahrMonat: { jahr, monat },
  });

  let sollstundenSum = 0;
  if (type == 'umsatz') {
    planwerte.forEach((planwert) => {
      if (planwert.umsatzPlan.umsatz) {
        const defaultStunden = planwert.umsatzPlan.umsatz.stunden ?? 0;
        sollstundenSum = defaultStunden + sollstundenSum;
      }
    });
  } else {
    planwerte.forEach((planwert) => {
      if (planwert.absatzPlan.absatz) {
        const defaultStunden = planwert.absatzPlan.absatz.stunden ?? 0;
        sollstundenSum = defaultStunden + sollstundenSum;
      }
    });
  }

  const comparisonValue = zielSollstunden
    ? zielSollstunden.aushilfen + zielSollstunden.festangestellte
    : 0;

  return (
    <div className="flex flex-col gap-2 w-1/3">
      <div className="flex gap-2 items-center">
        <FontAwesomeIcon
          icon={faCircle}
          className="text-primary-blue-600 h-3 w-3"
        />
        <div className="text-xs font-semibold text-neutrals-700">
          {t('domain.controlling.umsatzplanung.table.zielstunden')}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <ZielstundenField
          value={sollstundenSum}
          className="[&>div:nth-child(odd)]:text-2xl [&>div:nth-child(even)]:text-base [&>div:nth-child(odd)]:font-extrabold [&>div:nth-child(even)]:font-normal"
        />
        <Abweichung
          tooltipMessage={t(
            'domain.controlling.umsatzplanung.planung.tooltip.abweichungZuSollstunden',
          )}
          baseValue={sollstundenSum}
          comparisonValue={comparisonValue}
          type="stunden"
          unit={t('general.currency.EUR')}
        />
      </div>
    </div>
  );
}
